import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Settings,
  Video,
  Clock,
  Share2,
  Users,
  X,
  Save
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import InteractiveCalendar from '../components/InteractiveCalendar';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { CalendarEvent, ClientRecordingPreferences } from '../types/calendar';
import { Contenuto, StatoContenuto } from '../types';
import toast from 'react-hot-toast';
import { formatDateForInput, parseDateFromInput, createDateWithTime, validateDateMonth } from '../utils/dateUtils';

const NewCalendar: React.FC = () => {
  console.log('🚀 NewCalendar component loaded!');
  const { user } = useAuth();
  const { 
    state,
    loadContenuti,
    updateContenuto,
    updateContenutoStatus,
    cancelScheduling,
    getClienteById
  } = useData();
  
  console.log('🔍 NewCalendar state:', {
    user: user?.username,
    contenutiCount: state.contenuti.length,
    clientiCount: state.clienti.length
  });
  
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [clientPreferences, setClientPreferences] = useState<ClientRecordingPreferences[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    type: 'recording' as CalendarEvent['type'],
    clientId: '',
    description: '',
    priority: 'medium' as CalendarEvent['priority'],
    duration: 60
  });

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState<Contenuto | null>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [isScheduling, setIsScheduling] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isMobile && (showEventModal || showCreateModal || showScheduleModal)) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isMobile, showEventModal, showCreateModal, showScheduleModal]);

  // Carica eventi e preferenze dal localStorage
  useEffect(() => {
    const savedEvents = localStorage.getItem('bymarke_calendar_events');
    const savedPreferences = localStorage.getItem('bymarke_client_preferences');
    
    if (savedEvents) {
      try {
        setEvents(JSON.parse(savedEvents));
      } catch (error) {
        console.error('Error loading events:', error);
      }
    }
    
    if (savedPreferences) {
      try {
        setClientPreferences(JSON.parse(savedPreferences));
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    } else {
      // Genera preferenze predefinite per i clienti esistenti
      const defaultPreferences = state.clienti.map(cliente => ({
        clientId: cliente.id,
        preferredDays: [1, 2, 3, 4, 5], // Lun-Ven
        preferredTimeSlots: [
          { start: '09:00', end: '12:00' },
          { start: '14:00', end: '17:00' }
        ],
        contentPerWeek: 3,
        notes: ''
      }));
      setClientPreferences(defaultPreferences);
    }
  }, [state.clienti]);

  // Salva eventi nel localStorage
  useEffect(() => {
    localStorage.setItem('bymarke_calendar_events', JSON.stringify(events));
  }, [events]);

  // Salva preferenze nel localStorage
  useEffect(() => {
    localStorage.setItem('bymarke_client_preferences', JSON.stringify(clientPreferences));
  }, [clientPreferences]);

  // Genera eventi automatici basati sui contenuti esistenti
  useEffect(() => {
    const contentEvents: CalendarEvent[] = [];
    
    console.log('🔍 Analisi contenuti per generazione eventi:');
    console.log('📊 Contenuti totali:', state.contenuti.length);
    console.log('📊 Clienti totali:', state.clienti.length);
    
    state.contenuti.forEach((contenuto, index) => {
      console.log(`🔍 Contenuto ${index + 1}:`, {
        id: contenuto.id,
        titolo: contenuto.titolo,
        stato: contenuto.stato,
        clienteId: contenuto.clienteId,
        dataPubblicazione: contenuto.dataPubblicazione
      });
      
      const cliente = state.clienti.find(c => c.id === contenuto.clienteId);
      if (!cliente) {
        console.log(`❌ Cliente non trovato per contenuto ${contenuto.id}`);
        console.warn('⚠️ Contenuto con cliente mancante:', {
          contentId: contenuto.id,
          contentTitle: contenuto.titolo,
          clienteId: contenuto.clienteId,
          availableClients: state.clienti.map(c => ({ id: c.id, nome: c.nomeAzienda }))
        });
        
        // Log per debugging
        console.log('MISSING_CLIENT_IN_CALENDAR', {
          contentId: contenuto.id,
          clienteId: contenuto.clienteId,
          availableClientsCount: state.clienti.length
        });
        return;
      }

      // Evento di deadline per contenuti in bozza
      if (contenuto.stato === StatoContenuto.BOZZA) {
        console.log(`✅ Generando evento deadline per contenuto ${contenuto.id}`);
        const deadline = new Date();
        deadline.setDate(deadline.getDate() + 3); // 3 giorni da oggi
        
        const event: CalendarEvent = {
          id: `deadline-${contenuto.id}`,
          title: `${cliente.nomeAzienda}: ${contenuto.titolo}`,
          date: deadline.toISOString().split('T')[0],
          type: 'deadline',
          clientId: contenuto.clienteId,
          contentId: contenuto.id,
          description: `Scadenza per completare il contenuto per ${cliente.nomeAzienda}`,
          priority: 'high',
          status: 'scheduled',
          assignedTo: ['videomaker', 'video_editor']
        };

        contentEvents.push(event);
        console.log(`📅 Evento deadline creato:`, event);
      }

      // Evento di pubblicazione per contenuti programmati
      if (contenuto.dataPubblicazione) {
        console.log(`✅ Generando evento pubblicazione per contenuto ${contenuto.id}`);
        const pubDate = new Date(contenuto.dataPubblicazione);
        const event: CalendarEvent = {
          id: `publishing-${contenuto.id}`,
          title: `${cliente.nomeAzienda}: ${contenuto.titolo}`,
          date: pubDate.toISOString().split('T')[0],
          type: 'publishing',
          clientId: contenuto.clienteId,
          contentId: contenuto.id,
          description: `Pubblicazione programmata per ${cliente.nomeAzienda}`,
          priority: 'medium',
          status: 'scheduled',
          assignedTo: ['admin']
        };

        contentEvents.push(event);
        console.log(`📅 Evento pubblicazione creato:`, event);
      }

      // Evento di registrazione per contenuti in stato "testo_pronto"
      if (contenuto.stato === StatoContenuto.TESTO_PRONTO) {
        console.log(`✅ Generando evento registrazione per contenuto ${contenuto.id}`);
        const recordingDate = new Date();
        recordingDate.setDate(recordingDate.getDate() + 1); // 1 giorno da oggi
        
        const event: CalendarEvent = {
          id: `recording-${contenuto.id}`,
          title: `${cliente.nomeAzienda}: ${contenuto.titolo}`,
          date: recordingDate.toISOString().split('T')[0],
          type: 'recording',
          clientId: contenuto.clienteId,
          contentId: contenuto.id,
          description: `Registrazione video per ${cliente.nomeAzienda}`,
          priority: 'medium',
          status: 'scheduled',
          assignedTo: ['videomaker']
        };

        contentEvents.push(event);
        console.log(`📅 Evento registrazione creato:`, event);
      }
    });

    // Debug: log degli eventi generati
    console.log('📅 Eventi generati dai contenuti:', contentEvents);
    console.log('📅 Contenuti totali:', state.contenuti.length);
    console.log('📅 Clienti totali:', state.clienti.length);
    
    // Unisci eventi automatici con eventi manuali
    setEvents(prevEvents => {
      const manualEvents = prevEvents.filter(e => 
        !e.id.startsWith('deadline-') && 
        !e.id.startsWith('publishing-') && 
        !e.id.startsWith('recording-')
      );
      const allEvents = [...manualEvents, ...contentEvents];
      console.log('📅 Eventi totali nel calendario:', allEvents.length);
      return allEvents;
    });
  }, [state.contenuti, state.clienti]);

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
  };

  const handleCreateEvent = (date: Date) => {
    setSelectedDate(date);
    setNewEvent({
      title: '',
      type: 'recording',
      clientId: '',
      description: '',
      priority: 'medium',
      duration: 60
    });
    setShowCreateModal(true);
  };

  const handleManageContent = (content: Contenuto) => {
    console.log('🛠️ Gestione contenuto dal calendario:', content.id);
    setSelectedContent(content);

    const publicationDate = content.dataPubblicazione
      ? (content.dataPubblicazione instanceof Date
          ? content.dataPubblicazione
          : new Date(content.dataPubblicazione))
      : null;

    if (publicationDate && !Number.isNaN(publicationDate.getTime())) {
      setScheduleDate(formatDateForInput(publicationDate));
    } else {
      setScheduleDate('');
    }

    setShowScheduleModal(true);

    if (isMobile && 'vibrate' in navigator) {
      navigator.vibrate(30);
    }
  };

  const handleScheduleModalClose = () => {
    setShowScheduleModal(false);
    setSelectedContent(null);
    setScheduleDate('');
  };

  const handleScheduleSubmit = async () => {
    if (!selectedContent || !scheduleDate.trim()) {
      toast.error('Seleziona una data e ora valide per la programmazione');
      return;
    }

    const loadingToast = toast.loading('Programmazione in corso...');
    setIsScheduling(true);

    try {
      let scheduledDate = parseDateFromInput(scheduleDate);

      const [datePart, timePart] = scheduleDate.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes] = timePart.split(':').map(Number);
      const originalDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

      if (!validateDateMonth(originalDate, scheduledDate)) {
        scheduledDate = createDateWithTime(originalDate, hours, minutes);
      }

      await updateContenutoStatus(
        selectedContent.id,
        StatoContenuto.PROGRAMMATO,
        `Programmato per ${format(scheduledDate, 'dd/MM/yyyy HH:mm')}`
      );

      await updateContenuto(selectedContent.id, {
        dataPubblicazione: scheduledDate
      });

      await loadContenuti();

      toast.dismiss(loadingToast);
      toast.success('Programmazione aggiornata con successo!');

      handleScheduleModalClose();
    } catch (error) {
      console.error('Errore nella programmazione dal calendario:', error);
      toast.dismiss(loadingToast);
      toast.error('Errore durante la programmazione. Riprova.');
    } finally {
      setIsScheduling(false);
    }
  };

  const handleCancelScheduling = async () => {
    if (!selectedContent) {
      return;
    }

    if (!window.confirm('Sei sicuro di voler annullare la programmazione di questo contenuto?')) {
      return;
    }

    const loadingToast = toast.loading('Annullamento in corso...');
    setIsScheduling(true);

    try {
      await cancelScheduling(selectedContent.id, 'Programmazione annullata dal calendario');
      await loadContenuti();

      toast.dismiss(loadingToast);
      toast.success('Programmazione annullata con successo!');

      handleScheduleModalClose();
    } catch (error) {
      console.error('Errore nell\'annullamento della programmazione dal calendario:', error);
      toast.dismiss(loadingToast);
      toast.error('Errore nell\'annullamento della programmazione. Riprova.');
    } finally {
      setIsScheduling(false);
    }
  };

  const saveNewEvent = () => {
    if (!newEvent.title || !newEvent.clientId || !selectedDate) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    const event: CalendarEvent = {
      id: uuidv4(),
      title: newEvent.title,
      date: selectedDate.toISOString().split('T')[0],
      type: newEvent.type,
      clientId: newEvent.clientId,
      description: newEvent.description,
      priority: newEvent.priority,
      status: 'scheduled',
      assignedTo: user ? [user.role] : [],
      duration: newEvent.duration
    };

    setEvents(prev => [...prev, event]);
    setShowCreateModal(false);
    toast.success('Evento creato con successo!');

    // Invia notifica workflow se è un evento di registrazione
    if (newEvent.type === 'recording') {
      const cliente = state.clienti.find(c => c.id === newEvent.clientId);
      if (cliente) {
        console.log('Notifica workflow inviata per evento di registrazione:', {
          contentId: event.id,
          contentTitle: newEvent.title,
          clientName: cliente.nomeAzienda,
          fromUser: user?.username || 'Sistema',
          toRoles: ['videomaker', 'admin'],
          stage: 'uploaded'
        });
      }
    }
  };

  const completeEvent = (eventId: string) => {
    setEvents(prev => prev.map(event => 
      event.id === eventId 
        ? { ...event, status: 'completed' as const }
        : event
    ));
    toast.success('Evento completato!');
    setShowEventModal(false);
  };

  const deleteEvent = (eventId: string) => {
    setEvents(prev => prev.filter(event => event.id !== eventId));
    toast.success('Evento eliminato!');
    setShowEventModal(false);
  };

  const getEventTypeLabel = (type: string) => {
    switch (type) {
      case 'recording': return 'Registrazione';
      case 'deadline': return 'Scadenza';
      case 'publishing': return 'Pubblicazione';
      case 'meeting': return 'Riunione';
      default: return type;
    }
  };

  const getEventTypeIcon = (type: string) => {
    switch (type) {
      case 'recording': return <Video className="h-5 w-5" />;
      case 'deadline': return <Clock className="h-5 w-5" />;
      case 'publishing': return <Share2 className="h-5 w-5" />;
      case 'meeting': return <Users className="h-5 w-5" />;
      default: return <CalendarIcon className="h-5 w-5" />;
    }
  };

  // Filtra eventi in base al ruolo utente
  const getUserEvents = () => {
    if (!user) return events;
    
    return events.filter(event => {
      if (user.role === 'admin') return true;
      if (user.role === 'videomaker') {
        return event.type === 'recording' || event.type === 'deadline';
      }
      if (user.role === 'video_editor') {
        return event.type === 'deadline' || event.assignedTo?.includes('video_editor');
      }
      return false;
    });
  };

  const userEvents = getUserEvents();

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Responsive header */}
      <div className="flex flex-col space-y-3 sm:space-y-4 lg:space-y-0 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 leading-tight truncate">
            Calendario Editoriale
          </h1>
          <p className="text-gray-600 mt-0.5 sm:mt-1 text-xs sm:text-sm md:text-base leading-relaxed line-clamp-2 sm:line-clamp-none">
            Gestisci registrazioni, scadenze e pubblicazioni
          </p>
        </div>

        {/* Responsive action buttons */}
        <div className="flex flex-row gap-2 sm:gap-3 w-full lg:w-auto">
          <ProtectedRoute allowedRoles={['admin']}>
            <button
              className="flex-1 lg:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 min-h-[44px] bg-gray-100 text-gray-700 rounded-lg sm:rounded-xl hover:bg-gray-200 transition-all active:scale-95 font-medium text-sm"
            >
              <Settings className="h-4 w-4 flex-shrink-0" />
              <span className="hidden xs:inline sm:hidden md:inline truncate">Preferenze</span>
              <span className="xs:hidden">Pref.</span>
              <span className="hidden sm:inline md:hidden">Pref.</span>
            </button>
          </ProtectedRoute>

          <button
            onClick={() => handleCreateEvent(new Date())}
            className="flex-1 lg:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 min-h-[44px] bg-yellow-500 text-black rounded-lg sm:rounded-xl hover:bg-yellow-600 transition-all active:scale-95 font-medium text-sm"
          >
            <Plus className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Nuovo Evento</span>
          </button>
        </div>
      </div>

      {/* Responsive statistics - horizontal scroll on small mobile */}
      <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0 scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="grid grid-cols-4 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4 min-w-[400px] sm:min-w-0">
          <div className="glass-card p-2 sm:p-3 md:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-1 sm:gap-2 md:gap-3">
              <div className="p-1.5 sm:p-2 bg-red-100 rounded-md sm:rounded-lg flex-shrink-0">
                <Video className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-red-600" />
              </div>
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 truncate">Registr.</p>
                <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                  {userEvents.filter(e => e.type === 'recording' && e.status === 'scheduled').length}
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card p-2 sm:p-3 md:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-1 sm:gap-2 md:gap-3">
              <div className="p-1.5 sm:p-2 bg-orange-100 rounded-md sm:rounded-lg flex-shrink-0">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-orange-600" />
              </div>
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 truncate">Scadenze</p>
                <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                  {userEvents.filter(e => e.type === 'deadline' && e.status === 'scheduled').length}
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card p-2 sm:p-3 md:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-1 sm:gap-2 md:gap-3">
              <div className="p-1.5 sm:p-2 bg-green-100 rounded-md sm:rounded-lg flex-shrink-0">
                <Share2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-green-600" />
              </div>
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 truncate">Pubblic.</p>
                <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                  {userEvents.filter(e => e.type === 'publishing' && e.status === 'scheduled').length}
                </p>
              </div>
            </div>
          </div>

          <div className="glass-card p-2 sm:p-3 md:p-4">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-1 sm:gap-2 md:gap-3">
              <div className="p-1.5 sm:p-2 bg-blue-100 rounded-md sm:rounded-lg flex-shrink-0">
                <Users className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-blue-600" />
              </div>
              <div className="min-w-0 flex-1 text-center sm:text-left">
                <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 truncate">Riunioni</p>
                <p className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                  {userEvents.filter(e => e.type === 'meeting' && e.status === 'scheduled').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Calendario Interattivo */}
      <InteractiveCalendar
        events={userEvents}
        onEventClick={handleEventClick}
        onDateClick={handleDateClick}
        onCreateEvent={handleCreateEvent}
        onContentManage={handleManageContent}
      />

      {/* Modal Dettagli Evento - Responsive */}
      {showEventModal && selectedEvent && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999]">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="relative w-full sm:mx-auto sm:max-w-sm md:max-w-md lg:max-w-lg bg-white rounded-t-2xl sm:rounded-lg shadow-xl p-4 sm:p-6 max-h-[90vh] sm:max-h-[calc(100vh-200px)] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="flex items-start justify-between mb-4 gap-2">
              <div className="flex items-center space-x-2 min-w-0 flex-1">
                <span className="flex-shrink-0">{getEventTypeIcon(selectedEvent.type)}</span>
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 line-clamp-2">
                  {selectedEvent.title}
                </h3>
              </div>
              <button
                onClick={() => setShowEventModal(false)}
                className="text-gray-400 hover:text-gray-600 active:text-gray-800 p-1 -mr-1 flex-shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Tipo:</span>
                  <p className="text-sm sm:text-base text-gray-900">{getEventTypeLabel(selectedEvent.type)}</p>
                </div>

                <div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Data:</span>
                  <p className="text-sm sm:text-base text-gray-900">
                    {format(new Date(selectedEvent.date), isMobile ? 'dd/MM/yy' : 'dd MMMM yyyy')}
                  </p>
                </div>
              </div>

              <div>
                <span className="text-xs sm:text-sm font-medium text-gray-700">Cliente:</span>
                <p className="text-sm sm:text-base text-gray-900 truncate">
                  {state.clienti.find(c => c.id === selectedEvent.clientId)?.nomeAzienda || 'Cliente non trovato'}
                </p>
              </div>

              {selectedEvent.contentId && (
                <div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Contenuto:</span>
                  <p className="text-sm sm:text-base text-gray-900 line-clamp-2">
                    {state.contenuti.find(c => c.id === selectedEvent.contentId)?.titolo || 'Contenuto non trovato'}
                  </p>
                </div>
              )}

              {selectedEvent.description && (
                <div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700">Descrizione:</span>
                  <p className="text-sm sm:text-base text-gray-900">{selectedEvent.description}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 sm:gap-3">
                <div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700 block mb-1">Priorita:</span>
                  <span className={`
                    inline-block px-2 py-1 rounded text-xs font-medium
                    ${selectedEvent.priority === 'high' ? 'bg-red-100 text-red-800' : ''}
                    ${selectedEvent.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' : ''}
                    ${selectedEvent.priority === 'low' ? 'bg-green-100 text-green-800' : ''}
                  `}>
                    {selectedEvent.priority === 'high' && 'Alta'}
                    {selectedEvent.priority === 'medium' && 'Media'}
                    {selectedEvent.priority === 'low' && 'Bassa'}
                  </span>
                </div>

                <div>
                  <span className="text-xs sm:text-sm font-medium text-gray-700 block mb-1">Stato:</span>
                  <span className={`
                    inline-block px-2 py-1 rounded text-xs font-medium
                    ${selectedEvent.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}
                  `}>
                    {selectedEvent.status === 'completed' ? 'Completato' : 'Programmato'}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-6">
              {selectedEvent.status !== 'completed' && (
                <button
                  onClick={() => completeEvent(selectedEvent.id)}
                  className="flex-1 bg-green-500 text-white py-2.5 sm:py-2 px-4 rounded-lg hover:bg-green-600 active:bg-green-700 transition-colors font-medium text-sm sm:text-base"
                >
                  Completa
                </button>
              )}

              <ProtectedRoute allowedRoles={['admin']}>
                <button
                  onClick={() => deleteEvent(selectedEvent.id)}
                  className="flex-1 bg-red-500 text-white py-2.5 sm:py-2 px-4 rounded-lg hover:bg-red-600 active:bg-red-700 transition-colors font-medium text-sm sm:text-base"
                >
                  Elimina
                </button>
              </ProtectedRoute>

              {/* Mobile close button */}
              <button
                onClick={() => setShowEventModal(false)}
                className="sm:hidden flex-1 bg-gray-100 text-gray-700 py-2.5 px-4 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors font-medium text-sm"
              >
                Chiudi
              </button>
            </div>
          </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Crea Evento - Responsive */}
      {showCreateModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999]">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="relative w-full sm:mx-auto sm:max-w-sm md:max-w-md lg:max-w-lg bg-white rounded-t-2xl sm:rounded-lg shadow-xl p-4 sm:p-6 max-h-[90vh] sm:max-h-[calc(100vh-200px)] overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900">Nuovo Evento</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600 active:text-gray-800 p-1 -mr-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={(e) => { e.preventDefault(); saveNewEvent(); }} className="space-y-3 sm:space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Titolo *
                </label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-3 py-2.5 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Inserisci il titolo dell'evento"
                  required
                />
              </div>

              {/* Responsive grid for type and priority */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Tipo *
                  </label>
                  <select
                    value={newEvent.type}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, type: e.target.value as CalendarEvent['type'] }))}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="recording">Registrazione</option>
                    <option value="deadline">Scadenza</option>
                    <option value="publishing">Pubblicazione</option>
                    <option value="meeting">Riunione</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Priorita
                  </label>
                  <select
                    value={newEvent.priority}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, priority: e.target.value as CalendarEvent['priority'] }))}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="low">Bassa</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Cliente *
                </label>
                <select
                  value={newEvent.clientId}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, clientId: e.target.value }))}
                  className="w-full px-3 py-2.5 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                >
                  <option value="">Seleziona cliente</option>
                  {[...state.clienti]
                    .sort((a, b) => a.nomeAzienda.localeCompare(b.nomeAzienda, 'it', { sensitivity: 'base' }))
                    .map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nomeAzienda}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Data
                </label>
                <p className="text-sm sm:text-base text-gray-900 py-2 bg-gray-50 px-3 rounded-lg">
                  {selectedDate && format(selectedDate, isMobile ? 'dd/MM/yyyy' : 'dd MMMM yyyy')}
                </p>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Descrizione
                </label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2.5 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                  rows={isMobile ? 2 : 3}
                  placeholder="Descrizione opzionale dell'evento"
                />
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2.5 sm:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors text-sm sm:text-base"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-yellow-500 text-black py-2.5 sm:py-2 px-4 rounded-lg hover:bg-yellow-600 active:bg-yellow-700 transition-colors flex items-center justify-center space-x-2 text-sm sm:text-base font-medium"
                >
                  <Save className="h-4 w-4" />
                  <span>Salva</span>
                </button>
              </div>
            </form>
          </div>
          </div>
        </div>,
        document.body
      )}

      {showScheduleModal && selectedContent && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999]" onClick={handleScheduleModalClose}>
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <div
              className="relative w-full sm:mx-auto sm:max-w-sm md:max-w-md lg:max-w-lg bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-h-[90vh] sm:max-h-[calc(100vh-120px)] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-4 sm:p-6 border-b">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Gestisci programmazione</h3>
                <button
                  onClick={handleScheduleModalClose}
                  className="text-gray-400 hover:text-gray-600 active:text-gray-800 p-1 -mr-1"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4 overflow-y-auto max-h-[calc(90vh-200px)] sm:max-h-[calc(100vh-300px)]" style={{ WebkitOverflowScrolling: 'touch' }}>
                <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Cliente</p>
                  <p className="text-sm sm:text-base text-gray-900 font-semibold truncate">
                    {getClienteById(selectedContent.clienteId)?.nomeAzienda || 'Cliente non disponibile'}
                  </p>
                </div>

                <div>
                  <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Titolo contenuto</p>
                  <p className="text-sm sm:text-base text-gray-900 line-clamp-2">{selectedContent.titolo || 'Senza titolo'}</p>
                </div>

                {selectedContent.hook && (
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">Hook</p>
                    <p className="text-sm text-gray-900 whitespace-pre-line line-clamp-3">{selectedContent.hook}</p>
                  </div>
                )}

                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Data e ora di pubblicazione
                  </label>
                  <input
                    type="datetime-local"
                    value={scheduleDate}
                    onChange={(e) => setScheduleDate(e.target.value)}
                    className="w-full px-3 py-2.5 sm:py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {selectedContent.dataPubblicazione && (
                  <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
                    <p className="text-xs sm:text-sm font-medium text-blue-700 mb-1">Attualmente programmato per</p>
                    <p className="text-sm sm:text-base text-blue-700 font-semibold">
                      {format(new Date(selectedContent.dataPubblicazione), isMobile ? "dd/MM/yy 'alle' HH:mm" : "dd MMMM yyyy 'alle' HH:mm")}
                    </p>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-4 sm:px-6 py-4 flex flex-col gap-2 sm:gap-3">
                <button
                  type="button"
                  onClick={handleScheduleSubmit}
                  disabled={!scheduleDate || isScheduling}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base font-medium"
                >
                  <CalendarIcon className="h-4 w-4" />
                  <span className="truncate">
                    {selectedContent.stato === StatoContenuto.PROGRAMMATO ? 'Aggiorna' : 'Programma'}
                  </span>
                </button>

                {selectedContent.stato === StatoContenuto.PROGRAMMATO && (
                  <button
                    type="button"
                    onClick={handleCancelScheduling}
                    disabled={isScheduling}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 active:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    <X className="h-4 w-4" />
                    <span>Annulla programmazione</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleScheduleModalClose}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 active:bg-gray-200 text-sm sm:text-base"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NewCalendar;
